import {buildQueryString, DateHelper, parseNumber, parseString} from "@/shared/lib";

import type {QueryParams} from "../types";

/** Common rental booking parameters (guests, options) */
interface RentalBaseParams {
    adults?: number;
    children?: number;
    infants?: number;
    pets?: number;
    flexible?: boolean;
    nights?: number;
}

/** Parsed from URL - dates are strings, extends QueryParams for buildQueryString */
export interface RentalQueryParams extends RentalBaseParams, QueryParams {
    checkIn?: string;
    checkOut?: string;
}

/** Input for url() - accepts Date objects for convenience */
export interface RentalUrlParams extends RentalBaseParams {
    checkIn?: string | Date;
    checkOut?: string | Date;
}

/**
 * Rental detail route definition
 * Supports booking query parameters (dates, guests)
 */
export const rentalRoute = {
    path: "/rentals/[slug]",

    url: (slug: string, query?: RentalUrlParams): string => {
        let url = slug.startsWith("/") ? `/rentals${slug}` : `/rentals/${slug}`;
        if (query) {
            // Convert Date objects to strings in local timezone
            const normalizedQuery: RentalQueryParams = {
                ...query,
                checkIn: query.checkIn instanceof Date ? DateHelper.formatDate(query.checkIn) : query.checkIn,
                checkOut: query.checkOut instanceof Date ? DateHelper.formatDate(query.checkOut) : query.checkOut,
            };
            url += buildQueryString(normalizedQuery);
        }
        return url;
    },

    parse: (params: URLSearchParams): RentalQueryParams => {
        const flexibleParam = params.get("flexible");
        return {
            checkIn: parseString(params.get("checkIn")),
            checkOut: parseString(params.get("checkOut")),
            adults: parseNumber(params.get("adults")),
            children: parseNumber(params.get("children")),
            infants: parseNumber(params.get("infants")),
            pets: parseNumber(params.get("pets")),
            flexible: flexibleParam === "true",
            nights: parseNumber(params.get("nights")),
        };
    },

    /**
     * Extract rental ID from URL
     * Supports formats:
     * - /rentals/123
     * - /rentals/123?checkIn=...
     * - https://example.com/rentals/123
     * - Direct ID string: "123"
     */
    extractId: (url: string): string | null => {
        try {
            // Try to parse as URL
            const urlObj = new URL(url);
            const pathMatch = urlObj.pathname.match(/\/rentals?\/(\d+)/);
            if (pathMatch) {
                return pathMatch[1];
            }
        } catch {
            // If URL parsing fails, try direct pattern match
            const pathMatch = url.match(/\/rentals?\/(\d+)/);
            if (pathMatch) {
                return pathMatch[1];
            }

            // Check if it's just a number
            if (/^\d+$/.test(url.trim())) {
                return url.trim();
            }
        }

        return null;
    },

    /**
     * Extract multiple rental IDs from array of URLs
     */
    extractIds: (urls: Array<string>): Array<string> => {
        const ids = new Set<string>();

        urls.forEach((url) => {
            const id = rentalRoute.extractId(url);
            if (id) {
                ids.add(id);
            }
        });

        return Array.from(ids);
    },
};
